/**
 * Framework-level audit logs, kept for the item-set-editor session so returning
 * from authoring does not refetch every log and its S3 results file. The
 * authoring panel's resetAudits clears auditQuestionMem and item-level runs
 * clear parameter flags per audit, so a restore replays everything the fetch
 * applied rather than relying on any of it still being in place.
 */

export interface IFrameworkAuditParamFlag {
  questionId: number | string;
  dimension: string;
  checkId: string;
  message: string;
}

export interface IFrameworkAuditLogEntry {
  auditSlug: string;
  /** the log row as pushed to auditLogs */
  log: any;
  /** auditQuestionMem entries this log filled */
  memEntries: { [memKey: string]: any };
  paramCodeCollectors: { checkId: string; codes: string[] }[];
  paramFlags: IFrameworkAuditParamFlag[];
  blockingIssueCount: number;
  persistedIssueCount: number;
}

export interface IFrameworkAuditLogSnapshot {
  key: string;
  entries: IFrameworkAuditLogEntry[];
  overallMem: any;
}

export interface IFrameworkAuditLogSink {
  setMem(memKey: string, value: any): void;
  setOverallMem(value: any): void;
  pushLog(log: any): void;
  setParamCodeCollector(checkId: string, codes: string[]): void;
  addParamFlag(questionId: number | string, dimension: string, auditSlug: string, checkId: string, message: string): void;
  rebuildParamCodeCollectors(): void;
  markExpanded(auditSlug: string, blockingIssueCount: number, persistedIssueCount: number): void;
}

export const frameworkAuditLogCacheKey = (itemSetId: number | string, lang: string) => `${itemSetId}_${lang}`;

export const flattenParamFlaggedMap = (paramFlaggedMap: any): IFrameworkAuditParamFlag[] => {
  const flags: IFrameworkAuditParamFlag[] = [];
  Object.entries(paramFlaggedMap ?? {}).forEach(([questionId, dimensionMap]) => {
    Object.entries(dimensionMap ?? {}).forEach(([dimension, dimensionFlags]) => {
      ((dimensionFlags as any[]) ?? []).forEach(flag => {
        flags.push({ questionId, dimension, checkId: flag.checkId, message: flag.message });
      });
    });
  });
  return flags;
};

export const replayFrameworkAuditLogSnapshot = (snapshot: IFrameworkAuditLogSnapshot, sink: IFrameworkAuditLogSink): void => {
  sink.setOverallMem(snapshot.overallMem);
  let hasParamFlags = false;
  snapshot.entries.forEach(entry => {
    Object.keys(entry.memEntries).forEach(memKey => sink.setMem(memKey, entry.memEntries[memKey]));
    entry.paramCodeCollectors.forEach(({ checkId, codes }) => sink.setParamCodeCollector(checkId, codes));
    entry.paramFlags.forEach(flag => {
      hasParamFlags = true;
      sink.addParamFlag(flag.questionId, flag.dimension, entry.auditSlug, flag.checkId, flag.message);
    });
    sink.pushLog(entry.log);
    sink.markExpanded(entry.auditSlug, entry.blockingIssueCount, entry.persistedIssueCount);
  });
  if (hasParamFlags) {
    sink.rebuildParamCodeCollectors();
  }
};

/** One snapshot at a time: the editor shows one item set in one language. */
export class FrameworkAuditLogCache {
  private snapshot: IFrameworkAuditLogSnapshot | null = null;

  get(key: string): IFrameworkAuditLogSnapshot | null {
    return this.snapshot?.key === key ? this.snapshot : null;
  }

  store(snapshot: IFrameworkAuditLogSnapshot): void {
    this.snapshot = snapshot;
  }

  invalidate(): void {
    this.snapshot = null;
  }
}
